import type { Size } from "../../utils/controlStyles";
import { Button } from "../Button";
import { Chip } from "../Chip";

export interface Filter {
  id: string;
  label: string;
  value: string;
}

export interface FilterChipsProps {
  /** Active filters to display */
  filters: Filter[];
  /** Callback when a single filter is removed */
  onRemove: (id: string) => void;
  /** Callback to clear all filters */
  onClearAll?: () => void;
  /** Callback to open filter management UI */
  onManageFilters?: () => void;
  /** Maximum number of chips to show before "+ X more" */
  maxVisibleChips?: number;
  /** Display mode: "chips" shows individual removable chips, "button" shows compact count button */
  mode?: "chips" | "button";
  /** Show "Filters" button in chips mode to open filter panel */
  showManageButton?: boolean;
  /** Size variant */
  size?: Size;
  /** Additional CSS classes */
  className?: string;
}

/**
 * FilterChips
 *
 * Displays active filters as removable chips or a compact button.
 * This is a presentational component - responsive behavior is handled by the parent.
 *
 * Example usage:
 * ```tsx
 * <FilterChips
 *   filters={[
 *     { id: '1', label: 'Region', value: 'West' },
 *     { id: '2', label: 'Status', value: 'Active' }
 *   ]}
 *   onRemove={(id) => removeFilter(id)}
 *   onClearAll={clearAllFilters}
 *   onManageFilters={() => setDrawerOpen(true)}
 *   mode="chips"
 * />
 * ```
 */
export function FilterChips({
  filters,
  onRemove,
  onClearAll,
  onManageFilters,
  maxVisibleChips = 3,
  mode = "chips",
  showManageButton = true,
  size = "sm",
  className = "",
}: FilterChipsProps) {
  const hasFilters = filters.length > 0;
  const visibleFilters = filters.slice(0, maxVisibleChips);
  const hiddenCount = Math.max(0, filters.length - maxVisibleChips);
  const hasHidden = hiddenCount > 0;

  // Determine if we can show manage button based on mode
  const canShowButtonMode = mode === "button" && Boolean(onManageFilters);
  const canShowManageButton = mode === "chips" && showManageButton && Boolean(onManageFilters);

  // If no filters and no manage button to show, render nothing
  if (!hasFilters && !canShowButtonMode && !canShowManageButton) {
    return null;
  }

  // Button mode: show compact "X filters" button
  const filterButton = canShowButtonMode && (
    <Button variant="secondary" size={size} onClick={onManageFilters} className="whitespace-nowrap">
      {filters.length} {filters.length === 1 ? "filter" : "filters"}
    </Button>
  );

  // Chip mode: show filter chips
  const filterChips = (
    <>
      {canShowManageButton && (
        <Button variant="secondary" size={size} icon="Sliders" onClick={onManageFilters} className="whitespace-nowrap">
          Filters
        </Button>
      )}
      {hasFilters &&
        visibleFilters.map((filter) => (
          <Chip key={filter.id} variant="secondary" size={size} onRemove={() => onRemove(filter.id)} isRemovable>
            {filter.label}: {filter.value}
          </Chip>
        ))}
      {hasFilters && hasHidden && onManageFilters && (
        <Button
          variant="ghost"
          size={size}
          onClick={onManageFilters}
          className="text-text-muted hover:text-text-body whitespace-nowrap"
        >
          +{hiddenCount} more
        </Button>
      )}
      {hasFilters && onClearAll && (
        <Button
          variant="ghost"
          size={size}
          onClick={onClearAll}
          className="text-text-muted hover:text-text-body whitespace-nowrap"
        >
          Clear all
        </Button>
      )}
    </>
  );

  const content = mode === "button" ? filterButton : filterChips;

  return <div className={`flex flex-wrap items-center gap-2 ${className}`}>{content}</div>;
}
